import { AfterViewInit, Component, ElementRef, Input, QueryList, ViewChildren } from '@angular/core';
import { BehaviorSubject, combineLatest, concat, Observable } from 'rxjs';
import { Option } from '../../../core/models/option.model';
import { InputComponent } from '../input/input.component';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, takeUntil } from 'rxjs/operators';

/**
 * Multi Select component to show select input with label, required marker, and validation messages
 * For mare information https://optimistex.github.io/ngx-select-ex/
 *
 * @example
 *  <cfe-input-select
 *                    [id]="1"
 *                    [notMarkAsRequired]="true"
 *                    [options]="options"
 *                    [tabIndex]="6"
 *                    [disabled]="true"
 *                    [convertNull]="true"
 *                    [placeholder]="'The Placeholder'"
 *                    [control]="formControl"
 *                    [label]="'Label'">
 *  </cfe-input-select>
 */

@Component({
	selector: 'cfe-input-select',
	templateUrl: './input-select.component.html',
	styleUrls: ['./input-select.component.scss']
})
export class InputSelectComponent extends InputComponent implements AfterViewInit {
	/**
	 * Convert 'null' to null
	 */
	@Input() convertNull = true;

	/**
	 * An Options array
	 */
	@Input() options: Option[];

	/**
	 * Placeholder string
	 * hint: must be translated
	 */
	@Input() placeholder;

	/**
	 * Property to hold the currents value label
	 * This is needed to display the label via css and data attribute so that
	 * the faultless display of a value with ellipsis points is guaranteed
	 */
	selectedLabel$: Observable<string>;

	/**
	 * Property to hold the current placeholder
	 */
	placeholder$: Observable<string>;

	/**
	 * Subject to persist the placeholder input as observable
	 */
	private inputPlaceholderSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

	/**
	 * Observable consume the inputPlaceholderSubject
	 */
	get inputPlaceholder$(): Observable<string> {
		return this.inputPlaceholderSubject.asObservable().pipe(filter((value) => !!value));
	}

	/**
	 * Subject to persist the default placeholder as observable
	 */
	private defaultPlaceholder$: Observable<string>;

	/**
	 * Subject to persist the controls value as observable
	 */
	private valueSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

	/**
	 * Observable consume the valueSubject
	 */
	private get value$(): Observable<string> {
		return this.valueSubject.asObservable();
	}

	/**
	 * Observable to hold the label of the current selected option
	 */
	private valueLabel$: Observable<string>;

	/**
	 * Subject to persist the options as observable
	 */
	private optionsSubject: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>(null);

	/**
	 * Observable consume the optionsSubject
	 */
	public get options$(): Observable<Option[]> {
		return this.optionsSubject.asObservable();
	}

	// the main element of the complete select component
	// you can subscribe to each child element from the main html element
	@ViewChildren('inputSelect') inputSelect: QueryList<ElementRef>;

	/**
	 * Constructor
	 */
	constructor(protected translate: TranslateService) {
		super();
	}

	/**
	 * ngOnInit
	 */
	cfeOnInit() {
		// provide the translated default placeholder as long as there is no placeholder given
		this.defaultPlaceholder$ = this.translate.stream('inputSelect.placeholderPleaseSelect').pipe(takeUntil(this.inputPlaceholder$));

		// ues the default placeholder until the placeholder input was changed
		this.placeholder$ = concat(this.defaultPlaceholder$, this.inputPlaceholder$);

		// create value label when the value and the options are set
		this.valueLabel$ = combineLatest([this.value$, this.options$]).pipe(
			map(([value, options]) => {
				return this.getSelectedLabel(value, options);
			})
		);

		// if the control has a value, it is set as selected
		// and the corresponding option label is used as displayed value.
		// otherwise the placeholder is used
		this.selectedLabel$ = combineLatest([this.valueLabel$, this.placeholder$]).pipe(
			map(([valueLabel, placeholder]) => {
				return valueLabel ? valueLabel : placeholder;
			})
		);

		// Check whether the the value is a given/valid option.
		// If not, reset the value to force a new selection
		this.subscriptions.push(
			combineLatest([this.value$, this.options$.pipe(filter((options) => options?.length > 0))]).subscribe(([value, options]) => {
				// eslint-disable-next-line
				const isOption = options.find((option) => option.id == value);
				if (!isOption) {
					/**
					 * The form control and the element are reset if the value is not in options.
					 * The `setTimeout` defers this reset to the next tick. This behaviour solves problems when
					 * new options are given to the select component. It will prevent HTML <select>
					 * tag to select the first option. This makes a single option clickable,
					 * e.g. when we change the language.
					 *
					 * https://jira.dentsplysirona.com/browse/CCCPR-360
					 */
					setTimeout(() => {
						// The `emitEvent` has to be `false` to prevent loop
						this.control.reset(null, { emitEvent: false, onlySelf: true });
					}, 0);
				}
			})
		);

		if (this.disabled && !this.notMarkAsRequired) {
			this.markAsValid = true;
		}
	}

	ngAfterViewInit(): void {
		this.subscriptions.push(
			this.selectedLabel$.subscribe((label) => {
				this.inputSelect.first.nativeElement.dataset.content = label;
			})
		);
	}

	/**
	 * ngOnChanges
	 *
	 * @param changes
	 */
	cfeOnChanges(changes) {
		if (changes['control'] || changes['options'] || changes['placeholder']) {
			if (changes['control']) {
				this.valueSubject.next(this.control.value);
				// Subscribe value changes
				this.subscriptions.push(
					this.control.valueChanges.subscribe((value) => {
						this.valueSubject.next(value);
						// Convert 'null' to null
						if (this.convertNull && value === 'null') {
							this.control.setValue(null, { emitEvent: true });
						}
					})
				);
			}

			if (changes['options']) {
				this.optionsSubject.next(changes['options'].currentValue);
			}

			if (changes['placeholder']) {
				this.inputPlaceholderSubject.next(changes['placeholder'].currentValue);
			}
		}
	}

	private getSelectedLabel(value: string, options: Option[]): string | undefined {
		if (value && options) {
			// eslint-disable-next-line
			const selectedOption = options.find((option) => option.id == value);
			return selectedOption?.label;
		}
		return;
	}
}
